import React from "react";
import { useNavigate } from "react-router-dom";
import { Star, Eye, Heart } from "lucide-react";
import { useFavoris, useAuth, useLang } from "@/lib/api";
import { logoSrc } from "@/lib/media";
import { track } from "@/lib/track";
import "./SolutionCard.css";

// Carte de solution UNIFIÉE (accueil + index) : style « sol-card » de l'accueil
// (bande d'accent, logo, nom, catégorie, phrase d'accroche, pied « Voir la fiche »)
// alimentée par les vraies données + le contenu de l'index (note, vues, favori,
// badges, puces). Cliquable vers la fiche.
const BADGE_CLASS = { "vérifié": "verifie", "sponsorisé": "sponsor" };
const badgeClass = (b) => BADGE_CLASS[b] || b;
const badgeLabel = (b) => (b === "vérifié" ? "testé" : b);

export default function SolutionCard({ s, sponsorise = false, delay = 0 }) {
  const nav = useNavigate();
  const { toggle, isFav } = useFavoris();
  const { user } = useAuth();
  const { t } = useLang();

  const enAvant = sponsorise || s.sponsorise;
  // Accroche : phrase de valeur (comme l'accueil).
  const accroche = s.resume || s.slogan || s.description_courte || "";
  // Puces (index) : plateformes + pays, 2 max + « +N ».
  const tags = [...(s.plateformes || []), ...(s.pays ? [s.pays] : [])];
  const visibles = tags.slice(0, 2);
  const reste = tags.length - visibles.length;

  return (
    <div className={`sol-card${enAvant ? " sol-card-feat" : ""}`} onClick={() => { track(s.id, "interaction", "clic_liste"); nav(`/solution/${s.id}`); }}
      data-testid={sponsorise ? `sponsor-card-${s.id}` : `solution-card-${s.id}`}
      style={{ cursor: "pointer", transitionDelay: delay ? `${delay}s` : undefined }}>
      <div className={`accent ${enAvant ? "sponsor" : "reference"}`} />
      <div className="lavis" />
      <div className="contenu">
        {/* Logo + nom alignés, badges à droite */}
        <div className="sc-head">
          <div className={`logo${s.logo_url ? " has-img" : ""}`}>
            {s.logo_url ? <img src={logoSrc(s.logo_url)} alt={s.nom} loading="lazy" /> : (s.logo_txt || (s.nom || "?").slice(0, 2).toUpperCase())}
          </div>
          <h3>{s.nom}</h3>
          <div className="sc-badges">
            {s.est_exemple
              ? <span className="stamp exemple" title="Fiche fictive de démonstration">exemple</span>
              : <>{s.badge === "sponsorisé" && <span className="stamp verifie">testé</span>}
                <span className={`stamp ${badgeClass(s.badge)}`}>{badgeLabel(s.badge)}</span></>}
            {(s.badges || []).filter((b) => !s.badge || b !== s.badge).map((b) => (
              <span key={b} className={`stamp ${badgeClass(b)}`}>{badgeLabel(b)}</span>
            ))}
          </div>
        </div>

        <span className="cat">{s.fonction || s.secteur}</span>
        {/* Toujours rendus (même vides) → position figée des pills et de la méta. */}
        <span className="valeur">{accroche}</span>

        <div className="puces">
          {visibles.map((tg) => <span className="puce" key={tg}>{tg}</span>)}
          {reste > 0 && <span className="puce puce-more">+{reste}</span>}
        </div>

        <div className="sc-meta">
          <button className={`fav-heart ${isFav(s.id) ? "on" : ""}`} onClick={(e) => { e.stopPropagation(); if (user) { if (!isFav(s.id)) track(s.id, "interaction", "favori"); toggle(s.id); } else nav("/connexion?next=solutions"); }} data-testid={`fav-${s.id}`} aria-label={t("cat_fav_label")}>
            <Heart size={15} fill={isFav(s.id) ? "#F0A500" : "none"} />
          </button>
          {s.note > 0 && <span><Star size={12} className="star" fill="#F0A500" style={{ display: "inline", verticalAlign: "-2px" }} /> {s.note.toFixed(1)}</span>}
          {s.vues > 0 && <span><Eye size={12} style={{ display: "inline", verticalAlign: "-2px" }} /> {s.vues}</span>}
        </div>

        <div className="pied">
          <span className="voir-fiche">Voir la fiche</span>
        </div>
      </div>
    </div>
  );
}
